import { useMemo, useState } from "react"
import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"
import { subDays } from "date-fns"

import { EmptyState } from "src/components/EmptyState"
import { ParadiseGraphView } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/ParadiseGraphView"
import { ParadiseMutateGraphViewDialog } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/ParadiseMutateGraphViewDialog"
import { useParadiseGraphViews } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/useParadiseGraphViews"
import {
  LoadingWrapper,
  ParadiseLayout,
} from "src/components/Paradise/sharedStyles"
import { useFetchParadiseDevice } from "src/data/paradise/paradiseDevices/queries/paradiseDeviceQueries"
import { useAppLocation } from "src/hooks/useAppLocation"
import { MButton } from "src/ui/Button/MButton"
import { DateRangePicker } from "src/ui/DateRangePicker/DateRangePicker"
import GraphIcon from "src/ui/icons/graph-plot.svg"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { ITab } from "src/ui/Tabs/Tab"
import { TabPanel } from "src/ui/Tabs/TabPanel"
import { Tabs } from "src/ui/Tabs/Tabs"

export function ParadiseGraphViews({ deviceId }: { deviceId: string }) {
  const [mutateGraphViewDialogMode, setMutateGraphViewDialogMode] = useState<
    "add" | "edit" | null
  >(null)

  const [dateRange, setDateRange] = useState({
    startAt: subDays(new Date(), 7),
    endAt: new Date(),
  })

  const { hash } = useAppLocation()
  const activeTabId = hash.replace("#", "")

  const fetchParadiseDevice = useFetchParadiseDevice({
    deviceId,
  })

  const { graphViews, addGraphView, removeGraphView, renameGraphView } =
    useParadiseGraphViews()

  const activeGraphView = graphViews.find((view) => view.id === activeTabId)

  const tabs = useMemo<ITab[]>(() => {
    if (!fetchParadiseDevice.data) {
      return []
    }

    return graphViews.map((view) => ({
      id: view.id,
      label: view.name,
      view: (
        <ParadiseGraphView
          graphView={view}
          device={fetchParadiseDevice.data}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ),
    }))
  }, [graphViews, dateRange, fetchParadiseDevice.data])

  if (fetchParadiseDevice.isError) {
    return null
  }

  if (fetchParadiseDevice.isLoading) {
    return (
      <LoadingWrapper>
        <MSkeleton />
        <MSkeleton />
      </LoadingWrapper>
    )
  }

  return (
    <ParadiseLayout>
      <TopWrapper>
        <DateRangePicker
          key={JSON.stringify(dateRange)}
          startDate={dateRange.startAt}
          endDate={dateRange.endAt}
          onDateRangeChange={({ startDate, endDate }) => {
            setDateRange({
              startAt: startDate,
              endAt: endDate,
            })
          }}
          dateRangeProps={{
            minBookingDays: 2,
          }}
          defaultPresetKey="DAY"
        />

        <ButtonsWrapper>
          <MButton
            variant="minimal"
            size="small"
            onClick={() => removeGraphView(activeTabId)}
            color="destructive"
            disabled={!activeGraphView}
          >
            Remove Graph View
          </MButton>
          <MButton
            variant="minimal"
            size="small"
            onClick={() => {
              setMutateGraphViewDialogMode("edit")
            }}
            disabled={!activeGraphView}
          >
            Rename Graph View
          </MButton>
          <MButton
            onClick={() => {
              setMutateGraphViewDialogMode("add")
            }}
            variant="secondary"
            size="small"
          >
            Add Graph view
          </MButton>
        </ButtonsWrapper>
      </TopWrapper>

      {graphViews.length > 0 ? (
        <Tabs labels={tabs}>
          {tabs.map((tab) => (
            <StyledTabPanel key={tab.id}>{tab.view}</StyledTabPanel>
          ))}
        </Tabs>
      ) : (
        <EmptyState
          icon={
            <div>
              <GraphIcon width={86} />
            </div>
          }
          title="No Graph Views"
          body="Create your first graph view."
        />
      )}

      <ParadiseMutateGraphViewDialog
        open={mutateGraphViewDialogMode === "add"}
        onClose={() => setMutateGraphViewDialogMode(null)}
        onSave={addGraphView}
        title="Add Graph View"
      />

      {mutateGraphViewDialogMode === "edit" && (
        <ParadiseMutateGraphViewDialog
          open={true}
          onClose={() => setMutateGraphViewDialogMode(null)}
          onSave={(name) => renameGraphView(activeTabId, name)}
          title="Edit Graph View"
          initialName={activeGraphView?.name}
        />
      )}
    </ParadiseLayout>
  )
}

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.S};
`

const StyledTabPanel = styled(TabPanel)`
  margin-top: ${spacing.M};
`
